import { Column, EmptyDiv, FormContext, InfoDiv } from '@/components/SettingsLayout';
import SkeletonList from '@/components/SkeletonLoader/SkeletonList';
import { useGetUser } from '@/hooks/useGetUser';
import {
  useGetDefaultPricingConfigurationQuery,
  useGetMinimumPricingConfigurationQuery,
  useGetPricingConfigurationQuery,
  useUpsertOrganizationPricingConfigurationMutation,
} from '@/store/slices/pricingConfigurationSlice';
import { Text, Button, Icon, IconType } from '@/ui';
import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import PricingConfigurationForm from '@/pages/pricing-forms/PricingConfigurationForm';
import PricingDiffFooter from '@/pages/pricing-forms/PricingDiffFooter';
import { usePricingForm } from '@/pages/pricing-forms/usePricingForm';
import { valuesToFormRows } from '@/pages/pricing-forms/pricingFormMapping';

function OrganizationPricing() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { currentUser } = useGetUser();
  const [isReadOnly, setIsReadOnly] = useState(true);
  const companyArg = currentUser.companyId ? { organizationId: currentUser.companyId } : skipToken;

  const {
    data: defaults,
    isLoading: isDefaultsLoading,
    isError: isDefaultsError,
  } = useGetDefaultPricingConfigurationQuery(companyArg);
  const { data: minimums, isLoading: isMinimumsLoading } =
    useGetMinimumPricingConfigurationQuery(companyArg);
  const { data: existing, isLoading: isExistingLoading } = useGetPricingConfigurationQuery(
    id ? { organizationId: id } : skipToken,
  );

  const [upsertOrgPricingConfig, { isLoading: isSaving }] =
    useUpsertOrganizationPricingConfigurationMutation();

  const { activeCountry, setActiveCountry, submitHandler } = usePricingForm({
    baseline: defaults?.values ?? {},
    existing: existing?.values,
    submit: (nextValues) =>
      upsertOrgPricingConfig({
        organizationId: id ?? '',
        payload: { values: nextValues },
      }),
    messages: {
      success: t('Messages.organization_pricing_update_success'),
      error: t('Messages.organization_pricing_update_error'),
    },
  });

  const defaultFormValues = useMemo(
    () => ({ rows: valuesToFormRows(existing?.values ?? defaults?.values ?? {}) }),
    [existing, defaults],
  );

  if (isDefaultsLoading || isMinimumsLoading || isExistingLoading) {
    return <SkeletonList />;
  }

  if (isDefaultsError || !defaults) {
    return (
      <EmptyDiv>
        <Text size='s' weight='medium'>
          {t('Errors.error_loading_pricing')}
        </Text>
      </EmptyDiv>
    );
  }

  return (
    <FormContext submitHandler={submitHandler} mode='onChange' defaultValues={defaultFormValues}>
      <Column gapSize='24'>
        {isReadOnly && (
          <InfoDiv>
            <Text size='s' weight='light' color='base-grey'>
              {t('Pricing.organization_pricing_comment')}
            </Text>
            <Button
              type='button'
              variant='outline'
              onClick={() => setIsReadOnly(false)}
              testId='customize-pricing'
            >
              <Icon type={IconType.Pencil_Edit} width={24} height={24} />
              <Text size='s' weight='medium' color='base-grey'>
                {t('Pricing.customize')}
              </Text>
            </Button>
          </InfoDiv>
        )}
        <Column>
          <Text size='m' weight='semi-bold'>
            {t('Accounts.processing_fees')}
          </Text>
          <Text size='s' weight='light' color='base-grey'>
            {t('Accounts.processing_fees_comment')}
          </Text>
          <PricingConfigurationForm
            mode='org'
            defaults={defaults.values}
            minimums={minimums?.values}
            existingValues={existing?.values}
            isReadOnly={isReadOnly}
            activeCountry={activeCountry}
            onActiveCountryChange={setActiveCountry}
          />
        </Column>
        <PricingDiffFooter
          baseline={defaults.values}
          existing={existing?.values}
          isSaving={isSaving}
          isReadOnly={isReadOnly}
          activeCountry={activeCountry}
        />
      </Column>
    </FormContext>
  );
}

export default OrganizationPricing;
